import { motion } from "framer-motion";
import { ArrowUpRight, Users, TrendingUp, BarChart3, Clock } from "lucide-react";

const products = [
  {
    status: "live", title: "The HumanInLoop", tagline: "Real People. Real Mentorship. Real Hiring.",
    description: "In a world where AI-generated CVs game ATS filters, The HumanInLoop brings the human back into hiring. Professionals create real profiles with 2-min video pitches, corporates discover authentic talent.",
    url: "https://thehumaninloop.co.in", cta: "Visit thehumaninloop.co.in", icon: Users,
    bg: "bg-[#e8f5f0]", border: "border-[#2a9d8f]/20 hover:border-[#2a9d8f]/40", iconBg: "bg-[#2a9d8f]/10 border-[#2a9d8f]/20", iconColor: "text-[#2a9d8f]", tagColor: "text-[#2a9d8f]",
    badgeBg: "bg-[#2a9d8f]/10 text-[#2a9d8f] border-[#2a9d8f]/20",
  },
  {
    status: "coming_soon", title: "Capex Intelligence Loop", tagline: "AI-Powered Capital Expenditure Decision Intelligence",
    description: "Transforms how enterprises plan, evaluate, and track capex. Predictive ROI modelling, scenario analysis, and real-time cost overrun alerts — for CFOs and project heads.",
    url: null, cta: "Request Early Access", icon: TrendingUp,
    bg: "bg-[#eef5fc]", border: "border-[#4a8fc2]/20 hover:border-[#4a8fc2]/40", iconBg: "bg-[#4a8fc2]/10 border-[#4a8fc2]/20", iconColor: "text-[#4a8fc2]", tagColor: "text-[#4a8fc2]",
    badgeBg: "bg-[#d4a853]/10 text-[#b8923a] border-[#d4a853]/20",
  },
  {
    status: "coming_soon", title: "RebateIQ", tagline: "Stop losing margin to untracked dealer rebates.",
    tag: "For Manufacturers & Distributors",
    description: "Track tiered volume rebate schemes for your dealer/distributor network — without needing an ERP. Upload sales data, set your scheme, and get instant visibility into who's earned what.",
    url: null, cta: "Request a Demo", icon: BarChart3,
    bg: "bg-[#f3eef8]", border: "border-[#8b6aae]/20 hover:border-[#8b6aae]/40", iconBg: "bg-[#8b6aae]/10 border-[#8b6aae]/20", iconColor: "text-[#8b6aae]", tagColor: "text-[#8b6aae]",
    badgeBg: "bg-[#d4a853]/10 text-[#b8923a] border-[#d4a853]/20",
  },
];

export default function Products() {
  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32 relative warm-section-alt">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#2a9d8f] mb-4">Our Products</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>
            Products We're <span className="gradient-text">Building</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="text-base text-[#5c564e] mt-3 max-w-2xl">Beyond consulting, we build products that solve real industry problems at scale.</motion.p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
              className="group" data-testid={`product-card-${i}`}>
              <div className={`rounded-2xl border p-7 h-full flex flex-col ${p.bg} ${p.border} transition-all duration-500 hover:-translate-y-1`}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${p.iconBg}`}>
                    <p.icon className={`w-5 h-5 ${p.iconColor}`} strokeWidth={1.5} />
                  </div>
                  {p.status === "live" ? (
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${p.badgeBg}`}>Live</span>
                  ) : (
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${p.badgeBg} flex items-center gap-1`}><Clock size={9} /> Coming Soon</span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#2d2926] mb-1" style={{ fontFamily: 'Fraunces, serif' }}>{p.title}</h3>
                {p.tag && <p className="text-[10px] font-bold uppercase tracking-wider text-[#8a8078] mb-2">{p.tag}</p>}
                <p className={`text-sm font-medium ${p.tagColor} mb-3`}>{p.tagline}</p>
                <p className="text-sm text-[#5c564e] leading-relaxed mb-6 flex-1">{p.description}</p>
                {p.url ? (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`product-link-${i}`}
                    className="inline-flex items-center gap-2 bg-[#2a9d8f] text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-[#238b7f] transition-all w-fit">
                    {p.cta} <ArrowUpRight size={14} />
                  </a>
                ) : (
                  <a href="#contact" data-testid={`product-link-${i}`}
                    className={`inline-flex items-center gap-2 border rounded-full px-5 py-2.5 text-sm font-bold transition-all w-fit ${p.tagColor} border-current/30 hover:bg-current/5`}>
                    {p.cta} <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
